import { Button, Icons } from '@sede-enterprise/shell-ds-react-framework';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import Loader from 'src/UI/Loader';
import { sendNotification } from 'src/UI/Notification';
import Table from 'src/components/FieldNoms/FieldNomination/Table';
import type {
  IColumn,
  ITableFilterValue,
} from 'src/components/FieldNoms/FieldNomination/Table/Table.type';
import { COMMON_MESSAGES } from 'src/constants/Common';
import { GET_PARAMETER_DEFINITIONS, ParameterScope } from 'src/constants/EUMarkets';
import { get } from 'src/services/GenesisApi';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  fetchJobMessageTypes,
  fetchJobPeriods,
  type JobLookupRecord,
} from 'src/store/reducers/JobLookups';
import type { IParameterDefinition } from 'src/types/EUMarkets.type';
import type { ISelectOptions } from 'src/types/Common.type';
import { JobsModal } from './style';

export type JobRecord = Record<string, unknown>;

type JobsProps = {
  open: boolean;
  nodeName?: string;
  jobs: JobRecord[];
  isLoading: boolean;
  onClose: () => void;
};

type JobTableRow = {
  id: string | number;
  job: JobRecord;
  direction: string;
  type: string;
  sender: string;
  receiver: string;
  referenceCode: string;
  messagePeriod: string;
  granularity: string;
  validFrom: number | null;
  validTo: number | null;
  viewDetails: string;
};

type JobParameter = Record<string, unknown>;

const formatJobValue = (value: unknown): string => {
  if (value == null) {
    return '—';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
};

const formatDate = (value: number | null) =>
  value == null ? '—' : dayjs(value).format(COMMON_MESSAGES.DATE_DD_MMM_YYYY);

const getDateValue = (value: unknown): number | null => {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const parsedValue = dayjs(value);
  return parsedValue.isValid() ? parsedValue.valueOf() : null;
};

const getPeriodProperty = (period: unknown, propertyNames: string[]) => {
  if (typeof period !== 'object' || period === null) {
    return undefined;
  }
  const periodRecord = period as Record<string, unknown>;
  return propertyNames
    .map((propertyName) => periodRecord[propertyName])
    .find((value) => value != null);
};

// Extracts a representative display value (e.g. message type name, period label) from a lookup
// record so the same text is used both as the filter option and for matching row cell values.
// Falls back to the first non-id string field instead of dumping the whole record as JSON, in
// case the record doesn't use any of the expected key names.
const toLookupOptions = (items: JobLookupRecord[], keys: string[]): ISelectOptions[] => {
  const values = new Set<string>();
  items.forEach((item) => {
    const key = keys.find(
      (candidateKey) => item[candidateKey] != null && item[candidateKey] !== '',
    );
    if (key) {
      values.add(formatJobValue(item[key]));
      return;
    }
    const fallbackEntry = Object.entries(item).find(
      ([entryKey, entryValue]) =>
        typeof entryValue === 'string' &&
        entryValue !== '' &&
        !entryKey.toLowerCase().endsWith('id'),
    );
    if (fallbackEntry) {
      values.add(fallbackEntry[1] as string);
    }
  });
  return Array.from(values).map((value) => ({ id: value, name: value }));
};

// 'select' columns require an exact match against the chosen option; 'text' columns use substring search.
const FILTER_COLUMN_TYPES: Record<string, 'text' | 'select'> = {
  direction: 'select',
  type: 'select',
  sender: 'text',
  receiver: 'text',
  referenceCode: 'text',
  messagePeriod: 'select',
  granularity: 'select',
};

// Only the filterable (plain string) columns — excludes `id`/`job`/`validFrom`/`validTo`/`viewDetails`,
// which aren't strings and would otherwise widen row[key] to include JobRecord (an object).
type FilterableJobTableKey = keyof typeof FILTER_COLUMN_TYPES;

const rowMatchesFilters = (row: JobTableRow, filters: ITableFilterValue[]): boolean =>
  filters.every((filter) => {
    const { value } = filter;
    if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
      return true;
    }
    const cellValue = row[filter.key];
    const cellText = typeof cellValue === 'string' ? cellValue.toLowerCase() : '';
    const filterText = String(value).toLowerCase();
    return FILTER_COLUMN_TYPES[filter.key] === 'text'
      ? cellText.includes(filterText)
      : cellText === filterText;
  });

const getJobRows = (jobs: JobRecord[]): JobTableRow[] =>
  jobs.map((job, index) => {
    const period = job.period;
    const validFrom = getDateValue(
      job.validFrom ?? getPeriodProperty(period, ['validFrom', 'from', 'start']),
    );
    const validTo = getDateValue(
      job.validTo ?? getPeriodProperty(period, ['validTo', 'to', 'end']),
    );

    return {
      id: (job.id as string | number | undefined) ?? index,
      job,
      direction: job.isIncoming === true ? 'Incoming' : 'Outgoing',
      type: formatJobValue(job.messageType),
      sender: formatJobValue(job.senderOrganisationName),
      receiver: formatJobValue(job.receiverOrganisationName),
      referenceCode: formatJobValue(job.referenceCode),
      messagePeriod: formatJobValue(period),
      granularity: job.isHourlyGranularity === true ? 'Hour' : 'Day',
      validFrom,
      validTo,
      viewDetails: '',
    };
  });

const GRANULARITY_OPTIONS: ISelectOptions[] = [
  { id: 'Hour', name: 'Hour' },
  { id: 'Day', name: 'Day' },
];
const DIRECTION_OPTIONS: ISelectOptions[] = [
  { id: 'Incoming', name: 'Incoming' },
  { id: 'Outgoing', name: 'Outgoing' },
];

const getColumns = (
  onViewDetails: (job: JobRecord) => void,
  messageTypeOptions: ISelectOptions[],
  periodOptions: ISelectOptions[],
): IColumn[] => [
  {
    accessorKey: 'direction',
    header: 'Direction',
    sorting: true,
    type: 'select',
    optionsData: DIRECTION_OPTIONS,
    filter: { key: 'direction', label: 'Direction' },
  },
  {
    accessorKey: 'type',
    header: 'Type',
    sorting: true,
    type: 'select',
    optionsData: messageTypeOptions,
    filter: { key: 'type', label: 'Type' },
  },
  {
    accessorKey: 'sender',
    header: 'Sender',
    sorting: true,
    type: 'text',
    filter: { key: 'sender', label: 'Sender' },
  },
  {
    accessorKey: 'receiver',
    header: 'Receiver',
    sorting: true,
    type: 'text',
    filter: { key: 'receiver', label: 'Receiver' },
  },
  {
    accessorKey: 'referenceCode',
    header: 'Reference Code',
    sorting: true,
    type: 'text',
    filter: { key: 'referenceCode', label: 'Reference Code' },
  },
  {
    accessorKey: 'messagePeriod',
    header: 'Message Period',
    sorting: true,
    type: 'select',
    optionsData: periodOptions,
    filter: { key: 'messagePeriod', label: 'Message Period' },
  },
  {
    accessorKey: 'granularity',
    header: 'Granularity',
    sorting: true,
    type: 'select',
    optionsData: GRANULARITY_OPTIONS,
    filter: { key: 'granularity', label: 'Granularity' },
  },
  {
    accessorKey: 'validFrom',
    header: 'Valid From',
    sorting: true,
    cell: ({ getValue }) => formatDate(getValue() as number | null),
  },
  {
    accessorKey: 'validTo',
    header: 'Valid To',
    sorting: true,
    cell: ({ getValue }) => formatDate(getValue() as number | null),
  },
  {
    accessorKey: 'viewDetails',
    header: 'View Details',
    cell: ({ row }) => (
      <Button
        icon={<Icons.SearchCircle />}
        iconOnly
        variant='transparent'
        aria-label='View details'
        data-testid={`market-position-new-view-details-${row.original.id}`}
        onClick={() => onViewDetails(row.original.job)}
      />
    ),
  },
];

const JOB_SCOPES: Record<string, { incoming: ParameterScope; outgoing: ParameterScope }> = {
  DNSCP: {
    incoming: ParameterScope.JobIncomingDNSCP,
    outgoing: ParameterScope.JobOutgoingDNSCP,
  },
  AVAILY: {
    incoming: ParameterScope.JobIncomingAVAILY,
    outgoing: ParameterScope.JobOutgoingAVAILY,
  },
};

const getJobScopeId = (job: JobRecord): ParameterScope | null => {
  const messageType = typeof job.messageType === 'string' ? job.messageType : '';
  const scope = JOB_SCOPES[messageType.toUpperCase()];
  if (!scope) {
    return null;
  }
  return job.isIncoming === true ? scope.incoming : scope.outgoing;
};

const getParameterValue = (parameter: JobParameter) =>
  [
    parameter.valueString,
    parameter.valueLong,
    parameter.valueDouble,
    parameter.valueBoolean,
    parameter.valueListValue,
  ].find((value) => value != null && value !== '');

// A job can hold several entries for the same parameter name, so values are collected per name.
const getParameterValuesByName = (parameters: unknown): Map<string, string[]> => {
  const valuesByName = new Map<string, string[]>();
  if (!Array.isArray(parameters)) {
    return valuesByName;
  }

  parameters.forEach((parameter) => {
    if (typeof parameter !== 'object' || parameter === null) {
      return;
    }
    const parameterRecord = parameter as JobParameter;
    const value = getParameterValue(parameterRecord);
    if (value == null) {
      return;
    }
    const name = formatJobValue(parameterRecord.parameterName);
    const values = valuesByName.get(name) ?? [];
    const formattedValue = formatJobValue(value);
    if (!values.includes(formattedValue)) {
      values.push(formattedValue);
    }
    valuesByName.set(name, values);
  });

  return valuesByName;
};

const useParameterDefinitions = (job: JobRecord) => {
  const [definitions, setDefinitions] = useState<IParameterDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const scopeId = getJobScopeId(job);

  useEffect(() => {
    if (scopeId == null) {
      setDefinitions([]);
      return undefined;
    }

    let isCurrent = true;
    setIsLoading(true);
    get(`${GET_PARAMETER_DEFINITIONS}?scopeId=${scopeId}`)
      .then((response) => {
        if (isCurrent) {
          setDefinitions((response.data ?? []) as IParameterDefinition[]);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setDefinitions([]);
          sendNotification({ type: 'error', message: COMMON_MESSAGES.ERROR });
        }
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [scopeId]);

  return { definitions, isLoading };
};

const StatusIcon = ({ value }: { value: unknown }) =>
  value === true ? <Icons.Check aria-label='Yes' /> : <Icons.Cross aria-label='No' />;

const DetailItem = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className='job-detail-item'>
    <span className='job-detail-label'>{label}</span>
    <span className='job-detail-value'>{children}</span>
  </div>
);

const JobDetails = ({ job, onClose }: { job: JobRecord; onClose: () => void }) => {
  const { definitions, isLoading } = useParameterDefinitions(job);
  const valuesByName = getParameterValuesByName(job.parameters);
  const extendedProperties = definitions.map((definition) => ({
    name: definition.parameterName,
    value: valuesByName.get(definition.parameterName)?.join(', ') ?? '—',
  }));
  const hasStatus = job.modifiedTimeInUtc != null || job.modifiedBy != null;
  const hasValidity = job.validFrom != null || job.validTo != null;

  return (
    <section className='job-details' data-testid='market-position-new-job-details'>
      <div className='job-details-header'>
        <h3>Job details</h3>
        <Button
          icon={<Icons.CrossCircle />}
          iconOnly
          variant='transparent'
          aria-label='Hide job details'
          data-testid='market-position-new-hide-job-details'
          onClick={onClose}
        />
      </div>
      <div className='job-details-layout'>
        <div className='job-details-column'>
          <h4>Job properties</h4>
          <DetailItem label='Active'>
            <StatusIcon value={job.isActive} />
          </DetailItem>
          <DetailItem label='Type'>{formatJobValue(job.messageType)}</DetailItem>
          <DetailItem label='Message period'>{formatJobValue(job.period)}</DetailItem>
          <DetailItem label='Granularity'>
            {job.isHourlyGranularity === true ? 'Hour' : 'Day'}
          </DetailItem>
          <DetailItem label='Sender'>{formatJobValue(job.senderOrganisationName)}</DetailItem>
          <DetailItem label='Receiver'>{formatJobValue(job.receiverOrganisationName)}</DetailItem>
          <DetailItem label='Reference Code'>{formatJobValue(job.referenceCode)}</DetailItem>
          <DetailItem label='Inverse sign'>
            <StatusIcon value={job.isInverseSign} />
          </DetailItem>
        </div>
        <div className='job-details-column'>
          {isLoading && (
            <div className='jobs-loader'>
              <Loader />
            </div>
          )}
          {!isLoading && extendedProperties.length > 0 && (
            <div className='job-detail-section'>
              <h4>Extended Properties</h4>
              {extendedProperties.map((parameter) => (
                <DetailItem key={parameter.name} label={parameter.name}>
                  {parameter.value}
                </DetailItem>
              ))}
            </div>
          )}
          {hasStatus && (
            <div className='job-detail-section'>
              <h4>Status</h4>
              <DetailItem label='Modified Time'>{formatJobValue(job.modifiedTimeInUtc)}</DetailItem>
              <DetailItem label='Modified By'>{formatJobValue(job.modifiedBy)}</DetailItem>
            </div>
          )}
          {hasValidity && (
            <div className='job-detail-section'>
              <h4>Validity</h4>
              <DetailItem label='Valid From'>{formatDate(getDateValue(job.validFrom))}</DetailItem>
              <DetailItem label='Valid To'>{formatDate(getDateValue(job.validTo))}</DetailItem>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export const Jobs = ({ open, nodeName, jobs, isLoading, onClose }: JobsProps) => {
  const dispatch = useAppDispatch();
  const {
    jobPeriods,
    jobPeriodsLoaded,
    jobPeriodsLoading,
    jobMessageTypes,
    jobMessageTypesLoaded,
    jobMessageTypesLoading,
  } = useAppSelector((state) => state.JobLookups);
  const [selectedJob, setSelectedJob] = useState<JobRecord | null>(null);
  const [tableFilters, setTableFilters] = useState<ITableFilterValue[]>([]);
  const jobRows = useMemo(() => getJobRows(jobs), [jobs]);
  const filteredJobRows = useMemo(
    () => jobRows.filter((row) => rowMatchesFilters(row, tableFilters)),
    [jobRows, tableFilters],
  );
  const messageTypeOptions = useMemo(
    () =>
      toLookupOptions(jobMessageTypes, [
        'messageTypeName',
        'messageTypeDescription',
        'messageType',
        'description',
        'type',
        'name',
        'value',
      ]),
    [jobMessageTypes],
  );
  const periodOptions = useMemo(
    () => toLookupOptions(jobPeriods, ['periodName', 'period', 'name', 'value']),
    [jobPeriods],
  );

  useEffect(() => {
    setSelectedJob(null);
    setTableFilters([]);
  }, [jobs]);

  // Job lookup data (periods/message types) is fetched once, the first time the modal is opened
  // from any screen, and cached in redux so every later open reuses it instead of re-requesting.
  useEffect(() => {
    if (!open) {
      return;
    }
    if (!jobPeriodsLoaded && !jobPeriodsLoading) {
      dispatch(fetchJobPeriods());
    }
    if (!jobMessageTypesLoaded && !jobMessageTypesLoading) {
      dispatch(fetchJobMessageTypes());
    }
  }, [
    open,
    dispatch,
    jobPeriodsLoaded,
    jobPeriodsLoading,
    jobMessageTypesLoaded,
    jobMessageTypesLoading,
  ]);

  let content: React.ReactNode;
  if (isLoading) {
    content = (
      <div className='jobs-loader'>
        <Loader />
      </div>
    );
  } else if (selectedJob) {
    content = <JobDetails job={selectedJob} onClose={() => setSelectedJob(null)} />;
  } else {
    content = (
      <Table
        data={filteredJobRows}
        columns={getColumns(setSelectedJob, messageTypeOptions, periodOptions)}
        pagination={false}
        totalRecords={filteredJobRows.length}
        maxHeight={480}
        onTableStateChange={(state) => setTableFilters(state.filters)}
        getFilterPopupContainer={(triggerNode) =>
          triggerNode.closest('.jobs-modal') ?? document.body
        }
      />
    );
  }

  return (
    <JobsModal
      open={open}
      onClose={onClose}
      title={nodeName ? `Send and receive jobs: ${nodeName}` : 'Send and receive jobs'}
      isDraggable
      width={1200}
      maskClosable={false}
      className='jobs-modal'
      footer={
        <div className='button-style'>
          <Button variant='outlined' onClick={onClose} data-testid='market-position-new-jobs-close'>
            Close
          </Button>
        </div>
      }
      data-testid='market-position-new-jobs-modal'
    >
      {content}
    </JobsModal>
  );
};
